import { createSelector } from "@reduxjs/toolkit";
import { makeSelectors } from "../../core/make-selectors";
import { getExtendedCfi, isValidCfi } from "../../../../../../common/utils/cfi";
import { PageBreak } from "./types";
import { getCurrentPageBreakLabel } from "../nav/selectors";
import { removeDuplicates } from "../../../../../../common/utils/array";

const baseSelectors = makeSelectors("pagebreaks");

export const getPageBreaksLoadStatus = baseSelectors.getFetchStatus;

export const getPageBreaks = createSelector<any, any, PageBreak[]>(
  baseSelectors.getData,
  (pageBreaks) => {
    return pageBreaks
      ?.map((pageBreak) => {
        const cfi = isValidCfi(pageBreak.cfi)
          ? pageBreak.cfi
          : pageBreak.cfiWithoutAssertions;
        return { ...pageBreak, cfi, extendedCfi: getExtendedCfi(cfi) };
      })
      .filter((pageBreak) => isValidCfi(pageBreak.cfi));
  }
);

export const getCurrentPageBreakIndex = createSelector(
  (state) => getCurrentPageBreakLabel(state),
  (state) => getPageBreaks(state),
  (currentPageBreakLabel, pageBreaks) =>
    pageBreaks.findIndex((p) => p.label === currentPageBreakLabel)
);

export function getPageBreakIndexFromLabel(state, label) {
  const breaks = baseSelectors.getData(state);
  return breaks.findIndex((p) => p.label === label);
}

function isPageLabelNumeric(str) {
  if (typeof str != "string") {
    return false;
  }
  return !isNaN(str as any) && !isNaN(parseFloat(str));
}

export function getLastNumericPageBreakLabel(state) {
  const pageBreaks = baseSelectors.getData(state);
  if (pageBreaks && pageBreaks.length) {
    for (let i = pageBreaks.length - 1; i >= 0; i--) {
      const pageBreakLabel = pageBreaks[i].label;
      if (isPageLabelNumeric(pageBreakLabel)) {
        return pageBreakLabel;
      }
    }
  }
  return "";
}

/**
 * Get items from the page list by index
 * @param state
 * @param start Start index
 * @param end End index, inclusive
 */
export function getPageRangeFromIndex(state, start, end) {
  const pageList = baseSelectors.getData(state);
  if (pageList && start >= 0 && end >= 0 && end <= pageList.length) {
    return start === end ? [pageList[start]] : pageList.slice(start, end + 1);
  }
  return [];
}

/**
 * Get items from the page list by index
 * @param state
 * @param start Start label
 * @param end End label, inclusive
 */
export function getPageRangeFromLabels(state, start, end) {
  const startIndex = getPageBreakIndexFromLabel(state, start);
  const endIndex = getPageBreakIndexFromLabel(state, end);
  let pageRangeFromIndex = getPageRangeFromIndex(state, startIndex, endIndex);
  pageRangeFromIndex = removeDuplicates(
    pageRangeFromIndex,
    (page) => page.label
  );
  return pageRangeFromIndex;
}
